import { useCallback, useEffect, useState } from 'react';
import type { EventSetup, Question } from '../api/client.js';
import type { ControllerSession } from '../controller.js';

const toLocalInput = (iso: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

/** Admin: when, where, which rooms, which questions, the Zoom meeting, and the two sign-up links. */
export function SetupPanel({ session }: { session: ControllerSession }) {
  const eventId = session.engine?.record.eventId;
  const [draft, setDraft] = useState<EventSetup | null>(null);
  const [links, setLinks] = useState<{ student: string; speaker: string } | null>(null);
  const [zoom, setZoom] = useState<{ configured: boolean; ok: boolean; detail: string; fake?: boolean } | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const load = useCallback(() => {
    if (!eventId) return;
    void session.client.getSetup(eventId).then((r) => {
      setDraft(r.setup);
      setLinks(r.links);
    }).catch((e: unknown) => setMsg({ ok: false, text: String(e) }));
    void session.client.zoomStatus().then(setZoom).catch(() => setZoom(null));
  }, [session, eventId]);
  useEffect(load, [load]);

  if (!eventId || !draft) return <p className="muted">Loading…</p>;
  const set = <K extends keyof EventSetup>(k: K, v: EventSetup[K]) => setDraft({ ...draft, [k]: v });
  const run = async (label: string, fn: () => Promise<void>) => {
    setMsg(null);
    try {
      await fn();
      setMsg({ ok: true, text: label });
    } catch (e) {
      setMsg({ ok: false, text: (e instanceof Error ? e.message : String(e)).replace(/^[A-Z]+ \S+ -> \d+: /, '').replace(/^\{"error":"(.*)"\}$/, '$1') });
    }
  };
  const editable = (): Partial<EventSetup> => ({
    startsAt: draft.startsAt, durationMinutes: draft.durationMinutes, timezone: draft.timezone, rooms: draft.rooms, rounds: draft.rounds, roundMinutes: draft.roundMinutes,
    gapMinutes: draft.gapMinutes, firstRoundOffsetMinutes: draft.firstRoundOffsetMinutes, maxStudents: draft.maxStudents,
    studentQuestions: draft.studentQuestions, speakerQuestions: draft.speakerQuestions,
  });
  const save = () => run('Saved.', async () => setDraft((await session.client.saveSetup(eventId, editable())).setup));
  const setQ = (key: 'studentQuestions' | 'speakerQuestions', i: number, q: Partial<Question>) => {
    const list = draft[key].map((x, j) => (j === i ? { ...x, ...q } : x));
    set(key, list);
  };
  const copy = (text: string) => void navigator.clipboard?.writeText(text);

  const questionsEditor = (key: 'studentQuestions' | 'speakerQuestions', title: string) => (
    <div>
      <h3>{title}</h3>
      {draft[key].map((q, i) => (
        <div className="row" key={q.id}>
          <input type="text" aria-label={`${title} ${i + 1}`} value={q.label} maxLength={120} onChange={(e) => setQ(key, i, { label: e.target.value })} />
          <label className="check"><input type="checkbox" checked={q.required} onChange={(e) => setQ(key, i, { required: e.target.checked })} /> required</label>
          <button className="secondary" onClick={() => set(key, draft[key].filter((_, j) => j !== i))}>Remove</button>
        </div>
      ))}
      {draft[key].length < 2 && <button className="secondary" onClick={() => set(key, [...draft[key], { id: `q${draft[key].length + 1}`, label: '', required: false }])}>Add a question</button>}
    </div>
  );

  return (
    <div className="form" style={{ maxWidth: 'none' }}>
      {msg && <div className={`banner ${msg.ok ? 'banner-info' : 'banner-error'}`} role={msg.ok ? 'status' : 'alert'}>{msg.text}</div>}

      <h2>The evening</h2>
      <div className="row">
        <div>
          <label htmlFor="starts">Meeting starts</label>
          <input id="starts" type="datetime-local" value={toLocalInput(draft.startsAt)} onChange={(e) => set('startsAt', e.target.value ? new Date(e.target.value).toISOString() : null)} />
        </div>
        <div>
          <label htmlFor="dur">Length (minutes)</label>
          <input id="dur" type="number" min={15} max={480} value={draft.durationMinutes} onChange={(e) => set('durationMinutes', Number(e.target.value))} />
        </div>
        <div>
          <label htmlFor="tz">Timezone</label>
          <input id="tz" type="text" value={draft.timezone} onChange={(e) => set('timezone', e.target.value)} />
        </div>
      </div>
      <div className="row">
        <div><label htmlFor="rounds">Rounds</label><input id="rounds" type="number" min={1} max={20} value={draft.rounds} onChange={(e) => set('rounds', Number(e.target.value))} /></div>
        <div><label htmlFor="rm">Minutes per round</label><input id="rm" type="number" min={1} max={60} value={draft.roundMinutes} onChange={(e) => set('roundMinutes', Number(e.target.value))} /></div>
        <div><label htmlFor="gap">Changeover (minutes)</label><input id="gap" type="number" min={0} max={15} value={draft.gapMinutes} onChange={(e) => set('gapMinutes', Number(e.target.value))} /></div>
        <div><label htmlFor="off">Round 1 starts after (minutes)</label><input id="off" type="number" min={0} max={60} value={draft.firstRoundOffsetMinutes} onChange={(e) => set('firstRoundOffsetMinutes', Number(e.target.value))} /></div>
        <div><label htmlFor="max">Most students</label><input id="max" type="number" min={1} max={500} value={draft.maxStudents} onChange={(e) => set('maxStudents', Number(e.target.value))} /></div>
      </div>

      <h2>Rooms</h2>
      <p className="muted">Each room is a Zoom breakout room. Speakers and mentors choose one when they sign up. Capacity counts everyone in the room, mentors included.</p>
      {draft.rooms.map((r, i) => (
        <div className="row" key={r.roomId}>
          <input type="text" aria-label={`Room ${i + 1} name`} value={r.roomName} maxLength={60} onChange={(e) => set('rooms', draft.rooms.map((x, j) => (j === i ? { ...x, roomName: e.target.value } : x)))} />
          <input type="number" aria-label={`Room ${i + 1} capacity`} min={1} max={200} style={{ width: 90 }} value={r.capacity} onChange={(e) => set('rooms', draft.rooms.map((x, j) => (j === i ? { ...x, capacity: Number(e.target.value) } : x)))} />
          <button className="secondary" onClick={() => set('rooms', draft.rooms.filter((_, j) => j !== i))}>Remove</button>
        </div>
      ))}
      <div className="row"><button className="secondary" onClick={() => set('rooms', [...draft.rooms, { roomId: '', roomName: '', capacity: 8 }])}>Add a room</button></div>

      <h2>Questions on the sign-up pages</h2>
      <p className="muted">Everyone is asked their name and email. You can add up to two more questions for each group.</p>
      {questionsEditor('studentQuestions', 'Student question')}
      {questionsEditor('speakerQuestions', 'Speaker question')}
      <div className="row"><button className="primary" onClick={() => void save()}>Save</button></div>

      <h2>Zoom meeting</h2>
      {zoom && <p className={zoom.ok ? 'muted' : ''}>{zoom.ok ? zoom.detail : `Zoom is not connected: ${zoom.detail}`}{zoom.fake ? ' (development stand-in: links are not real)' : ''}</p>}
      {draft.meeting ? (
        <p>
          Meeting <strong>{draft.meeting.meetingId}</strong> created. Registration is on, so each person gets their own link. The host account must start the meeting.
        </p>
      ) : (
        <div className="row">
          <button className="primary" disabled={!zoom?.ok || !draft.startsAt} onClick={() => void run('Zoom meeting created.', async () => { await save(); setDraft((await session.client.createMeeting(eventId)).setup); })}>
            Create the Zoom meeting
          </button>
          <span className="muted">{!draft.startsAt ? 'Set the start time first.' : 'Registration, breakout rooms and the personal links are set up for you.'}</span>
        </div>
      )}

      <h2>Registration</h2>
      <div className="row">
        <button className={draft.registrationOpen ? 'secondary' : 'primary'} disabled={!draft.meeting} onClick={() => void run(draft.registrationOpen ? 'Registration closed.' : 'Registration is open.', async () => setDraft((await session.client.saveSetup(eventId, { registrationOpen: !draft.registrationOpen })).setup))}>
          {draft.registrationOpen ? 'Close registration' : 'Open registration'}
        </button>
        <span>{draft.registrationOpen ? 'Open: people can sign up now.' : draft.meeting ? 'Closed.' : 'Create the Zoom meeting first.'}</span>
      </div>
      {links && (
        <div>
          {(['student', 'speaker'] as const).map((k) => (
            <div key={k} className="row">
              <strong style={{ width: 90 }}>{k === 'student' ? 'Students' : 'Speakers'}</strong>
              <span className="copy">{links[k]}</span>
              <button className="secondary" onClick={() => copy(links[k])}>Copy</button>
              <button className="secondary" onClick={() => { if (confirm('Make a new link? The old one stops working immediately.')) void run('New link made. The old one no longer works.', async () => setLinks((await session.client.rotateLink(eventId, k)).links)); }}>New link</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
